// Account: sign in by email (Privy, or dev login locally), claim a handle, cloud-save status,
// the device conflict chooser, and the soft "save your progress across devices" prompt.
import { useState } from "preact/hooks";
import { signal } from "@preact/signals";
import { clientEnv } from "../../net/env";
import { canSignIn, session } from "../../net/session";
import { requestEmailCode, signInDev, signOut, verifyEmailCode } from "../../net/auth";
import { conflict, lastSynced, resolveConflict, signInPrompt, syncNow, syncState } from "../../net/cloud";
import { api } from "../../net/api";
import { gameState, showToast } from "../bridge";
import { t } from "../i18n";

export const accountOpen = signal(false);

export function AccountPanel() {
  const s = session.value;
  const [email, setEmail] = useState("");
  const [handle, setHandle] = useState(s?.handle ?? gameState()?.player.handle ?? "");
  const [busy, setBusy] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  if (!canSignIn()) return <p class="muted small">{t("account.offline")}</p>;
  if (!s)
    return (
      <div class="account">
        <p class="muted small">{t("account.why")}</p>
        <form
          class="row"
          style={{ gap: "8px", flexWrap: "wrap" }}
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            try {
              if (!clientEnv.privyAppId) {
                await signInDev(email);
                showToast("Signed in. Your save now follows you.");
              } else if (!codeSent) {
                await requestEmailCode(email);
                setCodeSent(true);
                showToast(`Code sent to ${email}`);
              } else {
                await verifyEmailCode(email, code);
                showToast("Signed in. Your save now follows you.");
              }
            } catch (err) {
              showToast(`Sign-in failed: ${String((err as Error).message)}`);
            } finally {
              setBusy(false);
            }
          }}
        >
          <input type="email" required placeholder="you@email.com" value={email} disabled={codeSent} onInput={(e) => setEmail(e.currentTarget.value)} aria-label="Email" style={{ flex: 1, minWidth: "180px" }} />
          {codeSent && <input inputMode="numeric" required placeholder="6-digit code" value={code} onInput={(e) => setCode(e.currentTarget.value.replace(/\D/g, "").slice(0, 6))} aria-label="Email code" style={{ width: "130px" }} />}
          <button class="btn primary" disabled={busy}>
            {clientEnv.privyAppId ? (codeSent ? t("account.signIn") : `✉️ ${t("account.continueEmail")}`) : t("account.signIn")}
          </button>
        </form>
        {!clientEnv.privyAppId && <p class="fine">Dev login (local only). Production uses Privy email codes.</p>}
      </div>
    );
  return (
    <div class="account">
      <div class="row-between">
        <span>
          ✅ {s.email ?? "Signed in"} {s.isAdmin ? <span class="tag up">admin</span> : null}
        </span>
        <button class="btn ghost" onClick={() => void signOut()}>
          {t("account.signOut")}
        </button>
      </div>
      <div class="row" style={{ gap: "8px" }}>
        <input value={handle} maxLength={20} onInput={(e) => setHandle(e.currentTarget.value.replace(/[^a-zA-Z0-9_]/g, ""))} aria-label="Handle" style={{ flex: 1 }} />
        <button
          class="btn"
          onClick={async () => {
            try {
              await api("PUT", "/me/handle", { handle, pfpSeed: gameState()?.player.pfp.seed });
              session.value = { ...s, handle };
              showToast(`You're @${handle} on leaderboards and in shared worlds.`);
            } catch (err) {
              showToast(String((err as Error).message));
            }
          }}
        >
          {s.handle ? "Change handle" : "Claim handle"}
        </button>
      </div>
      <div class="row-between small">
        <span class="muted">Cloud save: {syncState.value === "synced" ? `synced ${lastSynced.value}` : syncState.value}</span>
        <button class="link" onClick={() => void syncNow()}>
          Sync now
        </button>
      </div>
    </div>
  );
}

export function ConflictModal() {
  const c = conflict.value;
  if (!c) return null;
  const here = (d: string) => (d === c.local.deviceId ? "this device" : "your other device");
  return (
    <div class="modal-backdrop" role="dialog" aria-modal="true" aria-label="Choose a save">
      <div class="modal">
        <h3>Which life do you want to continue?</h3>
        <p class="muted small">Your cloud save is further along on another device.</p>
        <div class="person-actions">
          <button class="btn primary" onClick={() => void resolveConflict("cloud")}>
            ☁️ Continue from {here(c.cloud.deviceId)} (Day {c.cloud.day})
          </button>
          <button class="btn" onClick={() => void resolveConflict("local")}>
            📱 Keep {here(c.local.deviceId)} (Day {c.local.day})
          </button>
        </div>
        <p class="fine">The other copy stays in your last 5 cloud snapshots for recovery.</p>
      </div>
    </div>
  );
}

export function SignInPrompt() {
  const r = signInPrompt.value;
  if (!r || session.value || !canSignIn()) return null;
  return (
    <div class="signin-prompt" role="status">
      <span>{r === "rankup" ? "🎉 Nice climb! " : "📅 3 days in. "}Save your progress across devices?</span>
      <button class="btn primary" onClick={() => ((signInPrompt.value = null), (accountOpen.value = true))}>
        Sign in
      </button>
      <button class="link small" onClick={() => (signInPrompt.value = null)}>
        Later
      </button>
    </div>
  );
}

export function AccountModal() {
  if (!accountOpen.value) return null;
  return (
    <div class="modal-backdrop" onClick={() => (accountOpen.value = false)}>
      <div class="modal" role="dialog" aria-label="Account" onClick={(e) => e.stopPropagation()}>
        <h3>☁️ {t("account.title")}</h3>
        <AccountPanel />
        <button class="link small" onClick={() => (accountOpen.value = false)}>
          Close
        </button>
      </div>
    </div>
  );
}
